// @flow
export * from './crud'
export * from './methods'
export * from './selectors'
import { createAction } from 'redux-actions'
import { standardSet } from '../../util/redux'
import omit from 'lodash.omit'

/**
 * Exports schedule redux module in `Ducks` format
 * @see {@link https://github.com/erikras/ducks-modular-redux}
 * @module ducks/schedule
 */

// ACTION TYPES ///////////////////////////////////////////////////////////////

// Create
const CREATE_INIT = 'RACHIO/SCHEDULE/CREATE_INIT'
const CREATE_COMPLETE = 'RACHIO/SCHEDULE/CREATE_COMPLETE'
// Get
const SCHEDULE_GET_INIT = 'RACHIO/SCHEDULE/GET_INIT'
const SCHEDULE_GET_COMPLETE = 'RACHIO/SCHEDULE/GET_COMPLETE'
// Update
const UPDATE_INIT = 'RACHIO/SCHEDULE/UPDATE_INIT'
const UPDATE_COMPLETE = 'RACHIO/SCHEDULE/UPDATE_COMPLETE'
// Delete
const DELETE_INIT = 'RACHIO/SCHEDULE/DELETE_INIT'
const DELETE_COMPLETE = 'RACHIO/SCHEDULE/DELETE_COMPLETE'
// Copy
const COPY_INIT = 'RACHIO/SCHEDULE/COPY_INIT'
const COPY_COMPLETE = 'RACHIO/SCHEDULE/COPY_COMPLETE'
// Get Calendar
const GET_CALENDAR_INIT = 'RACHIO/SCHEDULE/GET_CALENDAR_INIT'
const GET_CALENDAR_COMPLETE = 'RACHIO/SCHEDULE/GET_CALENDAR_COMPLETE'
// Set main data (META is preserved) for a single or multiple schedules
const SET = 'RACHIO/SCHEDULE/SET'
// Get schedule preview for watering duration
const SCHEDULE_PREVIEW_INIT = 'RACHIO/SCHEDULE/PREVIEW_INIT'
const SCHEDULE_PREVIEW_COMPLETE = 'RACHIO/SCHEDULE/PREVIEW_COMPLETE'
// Set/disable schedule run skips (e.g. future feed skip/run buttons)
const SET_SKIP_INIT = 'RACHIO/SCHEDULE/SET_SKIP_INIT'
const SET_SKIP_COMPLETE = 'RACHIO/SCHEDULE/SET_SKIP_COMPLETE'

// ACTION CREATORS ////////////////////////////////////////////////////////////

// Create
export const scheduleCreateInit = createAction(CREATE_INIT)
export const scheduleCreateComplete = createAction(CREATE_COMPLETE)
// Get
export const scheduleGetInit = createAction(SCHEDULE_GET_INIT)
export const scheduleGetComplete = createAction(SCHEDULE_GET_COMPLETE)
// Update
export const scheduleUpdateInit = createAction(UPDATE_INIT)
export const scheduleUpdateComplete = createAction(UPDATE_COMPLETE)
// Delete
export const scheduleDeleteInit = createAction(DELETE_INIT)
export const scheduleDeleteComplete = createAction(DELETE_COMPLETE)
// Copy
export const scheduleCopyInit = createAction(COPY_INIT)
export const scheduleCopyComplete = createAction(COPY_COMPLETE)
// Get Calendar
export const calendarGetInit = createAction(GET_CALENDAR_INIT)
export const calendarGetComplete = createAction(GET_CALENDAR_COMPLETE)
// Set data
export const set = createAction(SET)
// Get schedule preview for watering duration
export const schedulePreviewInit = createAction(SCHEDULE_PREVIEW_INIT)
export const schedulePreviewComplete = createAction(SCHEDULE_PREVIEW_COMPLETE)
// Set/disable schedule run skips (e.g. future feed skip/run buttons)
export const scheduleSetSkipInit = createAction(SET_SKIP_INIT)
export const scheduleSetSkipComplete = createAction(SET_SKIP_COMPLETE)

// ACTION REDUCERS ////////////////////////////////////////////////////////////

export const initialState = {
  META: {
    createStatus: null // ['CREATING', action]
  }
}

/* \$FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.85.0. To view the error, delete this comment and run Flow. */
const schedule = (state = initialState, action = { type: '', payload: {} }) => {
  switch (action.type) {
    case CREATE_INIT:
      return {
        ...state,
        META: { ...state.META, createStatus: 'CREATING' }
      }
    case CREATE_COMPLETE:
      if (action.error) {
        return {
          ...state,
          META: { ...state.META, createStatus: action }
        }
      } else {
        return {
          ...state,
          META: { ...state.META, createStatus: action },
          [action.payload.schedule.id]: {
            ...action.payload.schedule,
            META: {}
          }
        }
      }
    case UPDATE_INIT:
      return {
        ...state,
        [action.payload.scheduleId]: {
          META: {
            ...(state[action.payload.scheduleId]
              ? state[action.payload.scheduleId].META
              : {}),
            ...{ updating: true }
          },
          ...state[action.payload.scheduleId]
        }
      }
    case UPDATE_COMPLETE:
      if (action.error) {
        return {
          ...state,
          [action.payload.scheduleId]: {
            META: {
              ...state[action.payload.scheduleId].META,
              ...{ updating: action }
            },
            ...state[action.payload.scheduleId]
          }
        }
      } else {
        return {
          ...state,
          [action.payload.schedule.id]: {
            META: {
              ...state[action.payload.schedule.id].META,
              ...{ updating: false }
            },
            ...action.payload.schedule
          }
        }
      }
    case DELETE_INIT:
      return {
        ...state,
        [action.payload.scheduleId]: {
          META: {
            ...state[action.payload.scheduleId].META,
            deleting: true
          },
          ...state[action.payload.scheduleId]
        }
      }
    case DELETE_COMPLETE:
      if (action.error) {
        return {
          ...state,
          [action.payload.scheduleId]: {
            META: {
              ...state[action.payload.scheduleId].META,
              ...{ deleting: action }
            },
            ...state[action.payload.scheduleId]
          }
        }
      } else {
        return omit(state, [action.payload.scheduleId])
      }
    case COPY_INIT:
      return {
        ...state,
        META: { ...state.META, copyStatus: 'COPYING' }
      }
    case COPY_COMPLETE:
      if (action.error) {
        return {
          ...state,
          META: { ...state.META, copyStatus: action }
        }
      } else {
        return {
          ...state,
          META: { ...state.META, copyStatus: action },
          [action.payload.schedule.id]: {
            ...action.payload.schedule,
            META: {}
          }
        }
      }
    case GET_CALENDAR_INIT:
      return {
        ...state,
        [action.payload.schedule_id]: {
          ...state[action.payload.schedule_id],
          META: {
            ...(state[action.payload.schedule_id] || {}).META,
            fetchingRuns: true
          }
        }
      }
    case GET_CALENDAR_COMPLETE:
      if (action.error) {
        return {
          ...state,
          [action.payload.scheduleId]: {
            ...state[action.payload.scheduleId],
            META: {
              ...state[action.payload.scheduleId].META,
              fetchingRuns: action
            }
          }
        }
      } else {
        return {
          ...state,
          [action.payload.scheduleId]: {
            ...state[action.payload.scheduleId],
            META: {
              ...(state[action.payload.scheduleId] || {}).META,
              runs: action.payload.runs,
              skips: action.payload.skips,
              fetchingRuns: false
            }
          }
        }
      }
    case SET:
      return standardSet(state, action)
    default:
      return state
  }
}

export default schedule
